/* eslint-disable max-lines-per-function */
import React from "react";
import { TextField, Tooltip, Grid, Autocomplete } from "@mui/material";
import { Formik, Form, useField } from "formik";
import * as yup from "yup";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import {
  InputWithErrorLabel,
  MaskedInputWithErrorLabel,
} from "components/haven-design/form/inputs";
import { SubmitButton } from "components/buttons";
import { useSnackbar } from "components/haven-design/snackbar";
import { extendedStatesList } from "components/haven-design/state-picker";
import { US_PHONE_MASK } from "utils/phone";
import { usePropertyInfo } from "./use-property-info";

// Custom ZIP mask function that only shows dash when typing extended ZIP
export const createZipMask = () => (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 5) {
    return [/\d/, /\d/, /\d/, /\d/, /\d/];
  }
  return [/\d/, /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/];
};

const validationSchema = yup.object({
  name: yup.string().required("Property name is required"),
  email: yup.string().email("Invalid email format").required("Email is required"),
  phone: yup.string().validUSPhoneNumber("Invalid phone number").required("Phone number is required"),
  phoneExt: yup.string(),
  address: yup.object({
    address1: yup.string().required("Address is required"),
    address2: yup.string(),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zip: yup
      .string()
      .required("Zip code is required")
      .matches(
        /^\d{5}(?:-\d{4})?$/,
        "ZIP code must be in the format 12345 or 12345-6789",
      ),
  }),
});

type StateSelectProps = {
  name: string;
  label: string;
  margin?: "none" | "dense" | "normal";
  size?: "small" | "medium";
  required?: boolean;
}

export const StateSelect: React.FC<StateSelectProps> =
({ name, label, margin, size, required }) => {
  const [field, meta, helpers] = useField(name);

  return (
    <Autocomplete
      options={extendedStatesList}
      getOptionLabel={(option) => option.label}
      value={extendedStatesList.find((opt) => opt.value === field.value) || null}
      onChange={(_, newValue) => {
        helpers.setValue(newValue ? newValue.value : "", true);
        helpers.setTouched(true, false);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          margin={margin}
          size={size}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
          name={name}
          onBlur={() => helpers.setTouched(true, true)}
        />
      )}
    />
  );
};

type Props = {
  path: string;
  propertyId: string;
}

const PropertyInfoPage: React.FC<Props> = ({ propertyId }) => {
  const { openSnackbar } = useSnackbar();
  const { property, loading, error, updateProperty } = usePropertyInfo(propertyId);

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!property) return <ServerError desc="Missing Data" />;

  return (
    <div>
      <Formik
        initialValues={{
          name: property.name || "",
          email: property.email || "",
          phone: property.phone || "",
          phoneExt: property.phoneExt || "",
          address: {
            address1: property.address?.address1 || "",
            address2: property.address?.address2 || "",
            city: property.address?.city || "",
            state: property.address?.state || "",
            zip: property.address?.zip || "",
          },
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          // Trim all string values before submitting
          const trimmedValues = {
            ...values,
            name: values.name?.trim(),
            email: values.email?.trim(),
            phone: values.phone?.trim(),
            phoneExt: values.phoneExt?.trim(),
            address: {
              ...values.address,
              address1: values.address.address1?.trim(),
              address2: values.address.address2?.trim(),
              city: values.address.city?.trim(),
              state: values.address.state?.trim(),
              zip: values.address.zip?.trim(),
            },
          };

          updateProperty(trimmedValues)
            .then(() => {
              openSnackbar({ message: "Property info updated", status: "success" });
              // Reset the form's dirty state after successful submission
              resetForm({ values: trimmedValues });
            })
            .catch(() => {
              openSnackbar({ message: "Error updating property info", status: "error" });
            });
        }}
      >
        {({ isValid, dirty }) => (
          <Form>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12 }}>
                <InputWithErrorLabel name="name" label="Property Name" margin="normal" required />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InputWithErrorLabel name="address.address1" label="Address 1" margin="normal" required/>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InputWithErrorLabel name="address.address2" label="Address 2" margin="normal"/>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InputWithErrorLabel name="address.city" label="City" margin="normal" required/>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <StateSelect name="address.state" label="State" margin="normal" required/>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <MaskedInputWithErrorLabel
                  name="address.zip"
                  label="Zip"
                  margin="normal"
                  mask={createZipMask()}
                  guide={false}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <MaskedInputWithErrorLabel
                  name="phone"
                  label="Phone"
                  margin="normal"
                  mask={US_PHONE_MASK}
                  guide={false}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InputWithErrorLabel name="phoneExt" label="Ext" margin="normal" />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <InputWithErrorLabel name="email" label="Email" margin="normal" required/>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {isValid ? (
                  <SubmitButton text="Save" disabled={!dirty} />
                ) : (
                  <Tooltip title="Please fill in all the required fields">
                    <span>
                      <SubmitButton text="Save" disabled={!dirty} />
                    </span>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PropertyInfoPage;
